/**
 * ================================================================================
 * ARQUIVO: resources/js/pages/SuperAdmin/Users.jsx
 * PROPÓSITO: Interface do Gestor de Utilizadores para o Super Admin.
 * CONCEITOS ENSINADOS:
 * - Chamadas à API protegida (axios/api)
 * - Tabela de dados responsiva e Modal formulário.
 * - Gerenciamento de estado complexo (create vs edit usando o mesmo formulário)
 * ================================================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Search, Shield, User, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { useFinance } from '../../context/FinanceContext';
import { useTheme } from '../../context/ThemeContext';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

export default function SuperAdminUsers() {
  const { user: currentUser } = useFinance();
  const { cardShape, tableStyle } = useTheme();

  // Estados principais
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Paginação e Filtros
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Estados do Modal
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Formulário
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    telegram_chat_id: '',
    password: '',
    is_super_admin: false
  });

  // =============================================================================
  // SEÇÃO: Fetch de Dados
  // OBJETIVO: Buscar a lista paginada de utilizadores na API
  // CONCEITOS: useEffect e useCallback para evitar re-renderizações desnecessárias
  // =============================================================================
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Fazemos o GET à rota do resource que acabamos de criar
      const res = await api.get('/admin/users', { params: { page } });
      setUsers(res.data.data || []);
      setLastPage(res.data.last_page || 1);
      setTotalUsers(res.data.total || 0);
    } catch (error) {
      console.error('Erro ao buscar utilizadores', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // =============================================================================
  // SEÇÃO: Ações de Formulário
  // OBJETIVO: Lidar com criação e edição no mesmo modal
  // =============================================================================
  const openNewUserModal = () => {
    setEditingUser(null);
    setForm({ name: '', email: '', phone: '', telegram_chat_id: '', password: '', is_super_admin: false });
    setErrorMsg('');
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    // Na edição, a senha começa vazia (só atualizamos se for preenchida)
    setForm({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      telegram_chat_id: user.telegram_chat_id || '',
      password: '',
      is_super_admin: user.is_super_admin === 1 || user.is_super_admin === true
    });
    setErrorMsg('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg('');

    try {
      // Se tiver edição, o método é PUT. Se for criação, método POST.
      if (editingUser) {
        await api.put(`/admin/users/${editingUser.id}`, form);
      } else {
        await api.post('/admin/users', form);
      }

      setShowModal(false);
      fetchUsers(); // Recarrega a tabela para ver a alteração
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Erro ao guardar utilizador.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    // PASSO 1: Proteção de UX (evitar clique acidental)
    if (!confirm('Tem a certeza absoluta que deseja eliminar este utilizador? Esta acção é irreversível.')) return;

    try {
      await api.delete(`/admin/users/${id}`);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao eliminar utilizador.');
    }
  };

  return (
    <div>
      <div className="page-header" style={{ flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Utilizadores</h1>
          <p className="page-subtitle">Gestão global do sistema ({totalUsers} registados)</p>
        </div>
        <button className="btn btn-primary" onClick={openNewUserModal}>
          <Plus size={18} /> Novo Utilizador
        </button>
      </div>

      <div className={`card table-${tableStyle} mb-md`} style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-primary)', paddingBottom: 12 }}>
              <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 13 }}>NOME</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 13 }}>EMAIL / TELEGRAM</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 13 }}>ESTATUTO</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 13 }}>CRIADO EM</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 13, textAlign: 'right' }}>AÇÕES</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 className="spinner" size={24} style={{ color: 'var(--text-tertiary)', margin: '0 auto' }} />
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
                  Nenhum utilizador encontrado.
                </td>
              </tr>
            ) : (
              users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <User size={16} color="var(--text-tertiary)" />
                      </div>
                      <div style={{ fontWeight: 600 }}>
                        {u.name}
                        {currentUser?.id === u.id && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--accent-blue)' }}>(Você)</span>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontSize: 14 }}>{u.email}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{u.telegram_chat_id ? `Telegram: ${u.telegram_chat_id}` : 'Sem Telegram'}</div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    {u.is_super_admin ? (
                      <span className="badge badge-active" style={{ background: 'var(--accent-purple-dim)', color: 'var(--accent-purple)' }}>
                        <Shield size={12} style={{ marginRight: 4 }} /> Super Admin
                      </span>
                    ) : (
                      <span className="badge" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)' }}>
                        Utilizador Padrão
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '16px', fontSize: 13, color: 'var(--text-tertiary)' }}>
                    {format(new Date(u.created_at), "dd MMM yyyy", { locale: pt })}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEditModal(u)} title="Editar">
                        <Pencil size={16} />
                      </button>

                      {/* Impede exclusão visual se for o próprio gajo logado */}
                      {currentUser?.id !== u.id && (
                        <button className="btn btn-ghost btn-icon" style={{ color: 'var(--accent-red)' }} onClick={() => handleDelete(u.id)} title="Eliminar">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação simples */}
      {lastPage > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</button>
          <span style={{ fontSize: 14, alignSelf: 'center' }}>Página {page} de {lastPage}</span>
          <button className="btn btn-secondary btn-sm" disabled={page === lastPage} onClick={() => setPage(page + 1)}>Próxima</button>
        </div>
      )}

      {/* MODAL CRIAR/EDITAR UTILIZADOR */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingUser ? 'Editar Utilizador' : 'Novo Utilizador'}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSave}>
              <div className="modal-body">
                {errorMsg && (
                   <div style={{ padding: 12, background: 'var(--accent-red-dim)', color: 'var(--accent-red)', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                     {errorMsg}
                   </div>
                )}

                <div className="form-group">
                  <label className="form-label">Nome Completo</label>
                  <input className="form-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">E-mail</label>
                    <input className="form-input" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Telegram Chat ID</label>
                    <input className="form-input" placeholder="123456789" value={form.telegram_chat_id} onChange={e => setForm({ ...form, telegram_chat_id: e.target.value.replace(/[^\d-]/g, '').slice(0, 64) })} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Senha {editingUser && '(Deixe em branco para não alterar)'}</label>
                  <input
                    className="form-input"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Mínimo 8 caracteres"
                    required={!editingUser}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                  />
                  {editingUser && <small style={{ color: 'var(--text-tertiary)', marginTop: 4, display: 'block' }}>A senha atual não é exibida por motivos de segurança.</small>}
                </div>

                {/* Restrição: Não pode remover seu próprio acesso admin se estiver a editar-se a si mesmo */}
                {!(editingUser && currentUser?.id === editingUser.id) && (
                  <div className="form-group" style={{ marginTop: 24 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', background: 'var(--bg-card)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)' }}>
                      <input
                        type="checkbox"
                        style={{ width: 18, height: 18, accentColor: 'var(--accent-purple)' }}
                        checked={form.is_super_admin}
                        onChange={e => setForm({ ...form, is_super_admin: e.target.checked })}
                      />
                      <div>
                        <div style={{ fontWeight: 600 }}>Privilégios de Super Administrador</div>
                        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Concede acesso total ao painel administrativo.</div>
                      </div>
                    </label>
                  </div>
                )}

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'A Guardar...' : editingUser ? 'Atualizar' : 'Criar Utilizador'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
